import React, { useEffect, useRef, useState, useContext } from 'react'
import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import { get } from '~/utils/api'
import { WebsocketContext } from './WebsocketConn'
import { Channel } from 'phoenix'
import { ChatContext } from './chatConn'

type Client = {
  id: number
  name: string
  avatar: string
}

type User = {
  id: number
  name: string
  avatar: string
}

type Message = {
  id: number
  client: boolean
  content: string
  user: number
}

export type Order = 'createdAsc' | 'createdDesc'

export type ChatOnList = {
  id: number
  createdAt: Date
  client: Client
  users: User[]
  message: Message | null
}

export type Filter = {
  dateFrom: string | null
  dateTo: string | null
  order: Order
}

type State = {
  chats: ChatOnList[]
  loadHook: () => void
  loadMore: () => void
  setSearch: (search: string) => void
  loading: boolean
  search: string
  filter: Filter
  setFilter: React.Dispatch<React.SetStateAction<Filter>>
  order: Order
  setOrder: (order: Order) => void
}

const defaultFilter: Filter = {
  dateFrom: null,
  dateTo: null,
  order: 'createdDesc',
}

export const ChatHistoryManagerContext = React.createContext<State>({
  chats: [],
  loadHook: () => {},
  loadMore: () => {},
  setSearch: () => {},
  setFilter: () => {},
  setOrder: () => {},
  loading: false,
  search: '',
  filter: defaultFilter,
  order: 'createdDesc',
})

type Props = { children: React.ReactNode }

const ChatHistoryManager: React.FC<Props> = ({ children }) => {
  const wsContext = useContext(WebsocketContext)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<Order>('createdDesc')
  const [filter, setFilter] = useState(defaultFilter)
  const [initialized, setInitialized] = useState(false)
  const [items, setItems] = useState<ChatOnList[]>([])
  const [channel, setChannel] = useState<Channel | undefined>()

  const { isConnected, channel: chatChannel } = useContext(ChatContext)

  const loadingRef = useRef(loading)
  const searchRef = useRef(search)
  const filterRef = useRef(filter)
  const itemsRef = useRef(items)

  loadingRef.current = loading
  filterRef.current = filter
  itemsRef.current = items

  const download = (params = {}, more = false) => {
    return new Promise<void>(r => {
      setLoading(true)
      loadingRef.current = true

      if (!more) {
        setItems([])
      }

      get<ChatOnList[]>(`chat/history`, {
        offset: more ? items.length : 0,
        search,
        ...filterRef.current,
        ...params,
      }).then(newItems => {
        if (more) {
          setItems(uniqBy([...items, ...newItems], e => e.id))
        } else {
          setItems(newItems)
        }

        setLoading(false)
        loadingRef.current = false
        r()
      })
    })
  }

  useEffect(() => {
    if (!isConnected) {
      return
    }

    chatChannel.on('chatHistory', (data: any) => {
      if (searchRef.current) {
        return
      }

      const chat = itemsRef.current.find(e => e.id === data.id)

      if (chat) {
        return
      }

      setItems(items => [
        { ...data, message: null, users: [window.c.user] },
        ...items,
      ])
    })
  }, [isConnected])

  const loadHook = () => {
    useEffect(() => {
      if (initialized) {
        return
      }

      download().then(() => {
        setInitialized(true)
      })
    }, [])
  }

  const [searchDebounce] = useState(() =>
    debounce(search => {
      download({ search })
    }, 500)
  )

  useEffect(() => {
    if (!initialized) {
      return
    }

    searchDebounce(search)
  }, [search])

  const loadMore = () => {
    if (loadingRef.current) {
      return
    }

    download({}, true)
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    download()
  }, Object.values(filter))

  return (
    <ChatHistoryManagerContext.Provider
      value={{
        chats: items,
        loadHook,
        loading,
        search,
        loadMore,
        setSearch,
        filter,
        setFilter,
        order,
        setOrder,
      }}
    >
      {children}
    </ChatHistoryManagerContext.Provider>
  )
}

export default ChatHistoryManager
